import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type { AsyncStorage } from "jotai/vanilla/utils/atomWithStorage";
import { useParams } from "next/navigation";
import { useSession } from "next-auth/react";

import type { CurrentUserModel } from "@/api";

import { getLoggedUser } from "@/actions/net-api/auth.net";
import { queryKeys } from "@/constants/query";
import { jotaiSessionStorage } from "@/data/storage";
import { isValidOrgId } from "@/lib/utils";

const orgIdAtom = atomWithStorage<string | null>(
  "orgId",
  null,
  jotaiSessionStorage as AsyncStorage<string | null>,
);

export const useLoggedUser = () => {
  const queryClient = useQueryClient();
  const [_orgId, setOrgId] = useAtom(orgIdAtom);
  const params = useParams();
  const orgId = params?.orgId as string;
  const { data: session, status, update } = useSession();
  /** Prefer stable user id so cache keys differ correctly when switching accounts (email can lag or collide in edge cases). */
  const sessionIdentity =
    session?.user?.id ?? session?.user?.email ?? "no-session";

  const prevSessionIdentityRef = useRef<string | null>(null);
  const lastCookieOrgIdRef = useRef<string | null>(null);

  useEffect(() => {
    const prev = prevSessionIdentityRef.current;
    if (prev === sessionIdentity) {
      return;
    }
    prevSessionIdentityRef.current = sessionIdentity;

    /** Prevent showing the previous user's `/me` payload after account switch or fresh login. */
    queryClient.removeQueries({ queryKey: ["getLoggedUser"], exact: false });
    /** Display name fallback in UserNav must not stick across accounts when email updates before session.name. */
    queryClient.removeQueries({ queryKey: ["userFullNameByEmail"], exact: false });
    /** Org-scoped person (menu display name) must not leak across accounts. */
    queryClient.removeQueries({ queryKey: ["getOrgPersonByUserId"], exact: false });

    if (sessionIdentity === "no-session") {
      setOrgId(null);
      lastCookieOrgIdRef.current = null;
      return;
    }

    const switchedAccount =
      prev !== null &&
      prev !== "no-session" &&
      prev !== sessionIdentity;
    const signedInAfterLogout = prev === "no-session";

    if (switchedAccount || signedInAfterLogout) {
      try {
        localStorage.removeItem("selected-org-id");
      } catch {
        /* ignore */
      }
      setOrgId(null);
      lastCookieOrgIdRef.current = null;
      if (typeof window !== "undefined") {
        document.cookie =
          "selected-org-id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
      }
    }

    /** Sidebar / admin chrome reads `useSession().user.role`; refetch JWT-backed session when identity changes. */
    void update().catch(() => {
      /* ignore */
    });
  }, [queryClient, sessionIdentity, setOrgId, update]);

  const { data: user, isLoading, isError, refetch } = useQuery<
    CurrentUserModel | null
  >({
    queryKey: queryKeys.getLoggedUser(sessionIdentity),
    queryFn: async (): Promise<CurrentUserModel | null> => {
      const u = await getLoggedUser();
      // 401 → null; React Query does not apply Next.js redirect() from server actions — clear session then sign-in.
      if (u === null && typeof window !== "undefined") {
        window.location.replace("/auth/sign-out?recover=1");
      }
      return u;
    },
    // Only fetch when authenticated; avoid repeated session/user POSTs
    enabled: status === "authenticated",
    staleTime: 5 * 60_000, // 5 minutes
    // After login, client navigation to /dashboard must refetch `/me`; stale cache + refetchOnMount false left users stuck on the loading shell.
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    let effectiveOrgId: string | null = null;

    // First path segment is only an org id when it is a GUID (e.g. /dashboard, /products, and
    // /project-templates match [orgId] but must never be used as org id).
    if (orgId && isValidOrgId(orgId)) {
      effectiveOrgId = orgId;
    } else {
      // Check localStorage first before defaulting to first org
      // This preserves user's previously selected org across sessions
      try {
        const storedOrgId = typeof window !== "undefined"
          ? localStorage.getItem("selected-org-id")
          : null;
        if (storedOrgId && isValidOrgId(storedOrgId)) {
          // Validate that the stored orgId is in the user's orgs list
          const isValidOrg = user?.userOrgs?.some((org) => org.id === storedOrgId);
          if (isValidOrg) {
            effectiveOrgId = storedOrgId;
          }
        }
      } catch {
        // Ignore localStorage errors
      }

      // Fallback to first org only if no valid stored orgId found
      if (!effectiveOrgId && user?.userOrgs?.[0]?.id) {
        effectiveOrgId = user.userOrgs[0].id;
      }
    }

    // Only update atom if the value actually changed to prevent infinite loops
    if (effectiveOrgId !== _orgId && effectiveOrgId !== null) {
      setOrgId(effectiveOrgId);
    } else if (!effectiveOrgId && _orgId !== null) {
      setOrgId(null);
    }

    // Set cookie and localStorage client-side to avoid server action revalidation/navigation
    // Only set if it's different from what we last set to avoid infinite loops
    if (effectiveOrgId && effectiveOrgId !== lastCookieOrgIdRef.current) {
      lastCookieOrgIdRef.current = effectiveOrgId;

      // Set cookie client-side using document.cookie (doesn't trigger revalidation)
      if (typeof window !== "undefined") {
        const maxAge = 60 * 60 * 24 * 7; // 7 days
        const secure = process.env.NODE_ENV === "production" ? "; secure" : "";
        document.cookie = `selected-org-id=${effectiveOrgId}; path=/; max-age=${maxAge}; sameSite=lax${secure}`;

        // Also save to localStorage for persistence across sessions
        try {
          localStorage.setItem("selected-org-id", effectiveOrgId);
          // Clean up old "orgId" key from localStorage if it exists (legacy)
          localStorage.removeItem("orgId");
        } catch {
          // Ignore localStorage errors (e.g., private browsing mode)
        }
      }
    }
    // Clear cookie and localStorage if orgId becomes null
    else if (!effectiveOrgId && lastCookieOrgIdRef.current !== null) {
      lastCookieOrgIdRef.current = null;
      if (typeof window !== "undefined") {
        document.cookie = "selected-org-id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
        try {
          localStorage.removeItem("selected-org-id");
        } catch {
          // Ignore localStorage errors
        }
      }
    }
  }, [orgId, _orgId, setOrgId, user?.userOrgs?.[0]?.id]); // Include _orgId to check if it changed

  return { user, isLoading, isError, refetch, orgId: _orgId };
};
